import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchExtLandingData } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { Button } from "../components/Button";
import { CategorySelectionStep } from "../components/CategorySelectionStep";
import { HeroGoalOptions } from "../components/HeroGoalOptions";
import Loader from "../components/Loader";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { FaBolt, FaWallet } from "react-icons/fa";
import { MdDiscount } from "react-icons/md";
import {
  CATEGORY_OPTION_MAP,
  CategoryOption,
  OTHER_GOAL_PREFIX,
  OTHER_OPTION_LABEL,
} from "../utils/categoryOptions";

interface ExtLandingData {
  eligibility: boolean;
  leadDetails?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    occupancy?: string;
    monthly_income?: string;
    dob?: Date;
    marital_status?: string;
    no_of_children?: string;
  };
  goal?: string;
}

type CategoryKey = keyof typeof CATEGORY_OPTION_MAP;

type HeroGoalOption = {
  label: string;
  icon: string;
  goal?: string;
  categoryKey?: CategoryKey;
};

const HERO_GOAL_OPTIONS: HeroGoalOption[] = [
  {
    label: "Fix or replace something at home",
    icon: "/images/pony/spanner.svg",
    categoryKey: "fix",
  },
  {
    label: "Help with bills",
    icon: "/images/pony/document.svg",
    goal: "I need help covering my household bills right now.",
  },
  {
    label: "Explore credit options",
    icon: "/images/pony/money.svg",
    goal: "I'm exploring credit options to manage an upcoming expense.",
  },
  {
    label: "Change in circumstances",
    icon: "/images/pony/tick.svg",
    goal: "My circumstances have changed and I need guidance on what to do next.",
  },
  {
    label: "Check benefits",
    icon: "/images/pony/pie.svg",
    goal: "I want to check if I'm missing out on any benefits.",
  },
  {
    label: "Manage debt or money worries",
    icon: "/images/pony/graph.svg",
    goal: "I need help managing debt or money worries.",
  },
];

const ExtLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leadData, setLeadData] = useState<ExtLandingData | null>(null);
  const [goalError, setGoalError] = useState("");
  const [currentStep, setCurrentStep] = useState<"hero" | "category">("hero");
  const [heroCustomGoal, setHeroCustomGoal] = useState("");
  const [selectedHeroLabel, setSelectedHeroLabel] = useState("");
  const [categoryKey, setCategoryKey] = useState<CategoryKey | null>(null);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState("");
  const [otherCategoryText, setOtherCategoryText] = useState("");
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const uid = searchParams.get("amj");
  const categoryContent = categoryKey
    ? CATEGORY_OPTION_MAP[categoryKey]
    : undefined;

  const sanitizeGoal = (text: string): string => {
    const div = document.createElement("div");
    div.textContent = text;
    const sanitized = div.textContent || "";
    const MAX_GOAL_LENGTH = 250;
    return sanitized.slice(0, MAX_GOAL_LENGTH);
  };

  const buildOtherGoal = (text: string): string => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return "";
    }
    return sanitizeGoal(`${OTHER_GOAL_PREFIX}${trimmedText}`);
  };

  useEffect(() => {
    setIsVisible(false);
    return () => setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    if (uid) {
      sessionStorage.setItem("ab_amj", uid);
    }
  }, [uid]);

  useEffect(() => {
    const fetchData = async () => {
      if (!uid) {
        UIUtils.showMessageOnSnackBar("Invalid URL");
        navigate("/");
        setLoading(false);
        return;
      }

      try {
        const response = await fetchExtLandingData(uid);
        const data: ExtLandingData = response.data;

        if (data.eligibility && data.leadDetails) {
          if (data.leadDetails.dob) {
            data.leadDetails.dob = new Date(data.leadDetails.dob);
          }
          setLeadData(data);
        } else {
          // Redirect to organic signup
          navigate("/");
          return;
        }
      } catch (err) {
        UIUtils.showMessageOnSnackBar(
          "Something went wrong. Please try again later."
        );
        UIUtils.trackException("External landing fetch error", err);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uid, navigate]);

  useEffect(() => {
    setSelectedCategoryOption("");
    setOtherCategoryText("");
  }, [categoryKey]);

  if (loading) {
    return <Loader message="Loading..." />;
  }

  if (!leadData) return null;

  const handleResolvedGoal = (goalValue: string, shouldSubmit = false) => {
    const sanitizedGoal = sanitizeGoal(goalValue);
    setLeadData((prev) => (prev ? { ...prev, goal: sanitizedGoal } : prev));
    setGoalError("");
    setCurrentStep("hero");
    setCategoryKey(null);
    setSelectedCategoryOption("");
    setOtherCategoryText("");
    if (shouldSubmit) {
      handleSignupClick(sanitizedGoal);
    }
  };

  const handleHeroGoalChange = (value: string) => {
    const sanitized = sanitizeGoal(value);
    setHeroCustomGoal(sanitized);
    setLeadData((prev) => (prev ? { ...prev, goal: sanitized } : prev));
    if (goalError) {
      setGoalError("");
    }
  };

  const handleHeroOptionSelect = (option: HeroGoalOption) => {
    setSelectedHeroLabel(option.label);
    if (goalError) {
      setGoalError("");
    }
    if (option.categoryKey && CATEGORY_OPTION_MAP[option.categoryKey]) {
      setCategoryKey(option.categoryKey);
      setCurrentStep("category");
      return;
    }
    if (option.goal) {
      handleResolvedGoal(option.goal, true);
    }
  };

  const handleHeroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedGoal = heroCustomGoal.trim();
    if (!trimmedGoal) {
      setGoalError("Please tell us what you need help with");
      return;
    }
    handleResolvedGoal(trimmedGoal);
    setHeroCustomGoal("");
  };

  const handleOtherCategoryInput = (value: string) => {
    setOtherCategoryText(value);
    if (goalError) {
      setGoalError("");
    }
  };

  const handleCategorySelection = (option: CategoryOption) => {
    setSelectedCategoryOption(option.label);
    if (option.label !== OTHER_OPTION_LABEL) {
      setOtherCategoryText("");
    }
    if (option.label !== OTHER_OPTION_LABEL) {
      const finalGoal =
        option.goal ||
        (categoryContent?.heading
          ? `${categoryContent.heading} - ${option.label}`
          : option.label);
      handleResolvedGoal(finalGoal, true);
    }
  };

  const handleCategoryContinue = () => {
    if (!categoryContent) {
      return;
    }
    if (!selectedCategoryOption) {
      UIUtils.showMessageOnSnackBar("Please pick an option to continue");
      return;
    }
    const selectedOption = categoryContent.options.find(
      (option) => option.label === selectedCategoryOption
    );
    if (!selectedOption) {
      UIUtils.showMessageOnSnackBar("Please pick an option to continue");
      return;
    }
    if (
      selectedOption.label === OTHER_OPTION_LABEL &&
      !otherCategoryText.trim()
    ) {
      setGoalError("Please tell us what needs fixing");
      return;
    }
    const finalGoal =
      selectedOption.label === OTHER_OPTION_LABEL
        ? buildOtherGoal(otherCategoryText)
        : selectedOption.goal;
    handleResolvedGoal(finalGoal, true);
  };

  const handleBackToHero = () => {
    setCurrentStep("hero");
    setCategoryKey(null);
    setSelectedHeroLabel("");
    setSelectedCategoryOption("");
    setOtherCategoryText("");
    setGoalError("");
    setHeroCustomGoal("");
  };

  const handleSignupClick = (goalOverride?: string) => {
    const goalToUse = goalOverride ?? leadData?.goal;
    if (!goalToUse) {
      setGoalError("Please tell us what you need help with");
      return;
    }
    navigate("/", {
      state: {
        leadDetails: leadData.leadDetails,
        fromExtLanding: true,
        goal: goalToUse,
      },
    });
  };

  if (currentStep === "category") {
    return (
      <div className="landing-static-page py-10">
        <div className="content-container">
          <CategorySelectionStep
            categoryContent={categoryContent}
            selectedCategoryOption={selectedCategoryOption}
            otherCategoryText={otherCategoryText}
            goalError={goalError}
            onBack={handleBackToHero}
            onCategorySelect={handleCategorySelection}
            onOtherInput={handleOtherCategoryInput}
            onContinue={handleCategoryContinue}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="content-container hero-section">
      {/* Main Content Section */}
      <div className="max-w-2xl w-full pt-2">
        {/* Hero Section */}
        <div className="bg-grey-20 rounded-20 mb-6">
          <h1 className="text-3xl font-bold mb-4">
            Your loan search didn't find a match — see how Afforda can help.
          </h1>
          <p className="text-lg mb-4">
            Afforda is 100% free - no subscriptions, no hidden fees.
          </p>
          <p className="mb-4">
            Tell us what you need. We will show you options to help you move
            forward.
          </p>
          
          {/* Champion Template: Goal Options First */}
          <HeroGoalOptions
            options={HERO_GOAL_OPTIONS}
            selectedLabel={selectedHeroLabel}
            onSelect={handleHeroOptionSelect}
          />
          
          <div className="mb-6">
            <form className="mt-5 space-y-2" onSubmit={handleHeroSubmit}>
              <textarea
                id="hero-goal-input"
                value={heroCustomGoal}
                onChange={(e) => handleHeroGoalChange(e.target.value)}
                placeholder="Type here for something else"
                className="form-input w-full h-24 border rounded-md focus:ring-2 focus:ring-blue-400 focus:border-transparent border-neutral-200"
              />
              {goalError && <p className="text-sm text-red">{goalError}</p>}
            </form>
            <Button
              text="Get started - it's free"
              variant="primary"
              onClick={handleSignupClick}
              type="button"
              className="w-full mt-4"
            />
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-grey-20 rounded-20 mb-6">
          <h2 className="text-xl font-bold text-neutral-800 mb-4">
            Take action today
          </h2>
          <div className="bg-white rounded-lg pb-6 pt-4 px-4 shadow-sm border border-neutral-200">
            <p className="text-lg font-semibold text-neutral-800 mb-2">
              Check if you're eligible for benefits - £22.7 billion goes
              unclaimed every year.
            </p>
            <p className="text-neutral-600 mb-4">
              Complete our quick benefits check form to see what you may be
              entitled to.
            </p>
            <Button
              text="See what you may qualify for"
              onClick={handleSignupClick}
              type="button"
              className="w-full"
            />
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">How Afforda helps</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <FaBolt className="w-8 h-8 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600 text-xl">
                  Quick benefits check
                </h4>
                <p>See what support you could claim in a few clicks.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <MdDiscount className="w-8 h-8 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600 text-xl">Instant savings</h4>
                <p>
                  Get exclusive discounts at your favourite high street stores.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <FaWallet className="w-6 h-6 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600 text-xl">Credit options</h4>
                <p>
                  We compare products from over 15 lenders to see if it's
                  possible to get you an offer.{" "}
                  <span className="font-bold">Representative 1333%APR</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtLanding;
